import React from 'react';
import Icon from '@mdi/react';
import { mdiPencil, mdiChatOutline, mdiChatAlert, mdiChatRemoveOutline, mdiCheckOutline, mdiAirplane, mdiAccountQuestionOutline, mdiCreditCardCheck, mdiCreditCardSyncOutline, mdiCreditCardClockOutline, mdiCreditCardRefund } from '@mdi/js';
import './BookingMiniCard.css';
import './ActionableIconButton.css';
import avatarPlaceholder from '../assets/avatar_user_5.png';

/**
 * BookingMiniCard - Compact booking card for inline display in day view
 * Shows avatar, name, time, status, and action icons
 */
const BookingMiniCard = ({
  booking,
  onConfirm,
  onEdit,
  onMessage,
  showTravelIcon = false,
  isManualBooking = false,
  hideEditIcon = false,
  compact = false
}) => {
  const isPending = booking.status === 'pending';
  const isUnlinkedManualBooking = isManualBooking && booking.studentId === '000000000000000000000000';

  // Format time slot
  const formatTime = (startTime, endTime) => {
    return `${startTime} - ${endTime}`;
  };

  const handleCheckboxClick = (e) => {
    e.stopPropagation();
    if (isPending && onConfirm) {
      onConfirm();
    }
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit();
    }
  };

  const handleMessageClick = (e) => {
    e.stopPropagation();
    if (onMessage) {
      onMessage();
    }
  };

  /**
   * Get message icon state based on 3-state system:
   * 1. No conversation (mdiChatRemoveOutline) - Gray
   * 2. Unread messages (mdiChatAlert) - Red - Student sent, instructor hasn't viewed yet
   * 3. All read (mdiChatOutline) - Gray - All caught up
   *
   * IMPORTANT: The bookings-list endpoint omits the full `messages[]` thread —
   * it only includes server-computed `lastMessage` (null when no thread
   * exists) and `unreadCount`.
   */
  const getMessageIconState = () => {
    const unreadCount = booking.unreadCount || 0;
    const hasMessages = booking.lastMessage != null || unreadCount > 0;

    // State 1: No conversation exists
    if (!hasMessages) {
      return {
        icon: mdiChatRemoveOutline,
        className: '',
        title: 'No messages'
      };
    }

    // State 2: Unread messages (red alert)
    if (unreadCount > 0) {
      return {
        icon: mdiChatAlert,
        className: 'icon-message-alert',
        title: `${unreadCount} unread message${unreadCount > 1 ? 's' : ''}`
      };
    }

    // State 3: All caught up (gray outline)
    return {
      icon: mdiChatOutline,
      className: '',
      title: 'All messages read'
    };
  };

  const messageIconState = getMessageIconState();

  return (
    <div
      className={`booking-mini-card ${isPending ? 'pending' : 'confirmed'}`}
      onClick={handleEditClick}
    >
      {/* Avatar */}
      <div className="mini-card-avatar">
        <img
          src={booking.studentInfo?.profileImageUrl || avatarPlaceholder}
          alt={booking.studentName || 'Student avatar'}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = avatarPlaceholder;
          }}
        />
      </div>

      {/* Info Section */}
      <div className="mini-card-info">
        <div className="mini-card-name">
          {booking.studentName || 'Unknown Student'}
        </div>
        <div className="mini-card-time">
          {formatTime(booking.timeSlot.startTime, booking.timeSlot.endTime)}
        </div>
      </div>

      {/* Icons Section */}
      <div className="mini-card-icons">
        {/* Payment Status Indicator — surfaces for any unpaid priced booking so the
            instructor sees the pending state on the calendar/list view. After-lesson
            bookings now show this too (the Request Payment modal handles the timing
            nuance via an in-modal hint). */}
        {booking.pricing && (booking.pricing.amount || 0) > 0 &&
         (booking.pricing.paymentStatus === 'paid' ||
          booking.pricing.paymentStatus === 'credited' ||
          booking.pricing.paymentStatus === 'refunded' ||
          (booking.pricing.paymentTiming === 'before-lesson') ||
          (booking.pricing.paymentTiming === 'after-lesson' && (booking.status === 'confirmed' || booking.status === 'completed'))) && (() => {
          // Four states (canonical payment-icon coding): returned as credit/refund
          // (turquoise refund icon — money question settled), paid externally (green
          // credit-card-sync — admin asserted cash/EFTPOS/bank transfer, reconcilable),
          // paid via Stripe / settled (SOLID green check), otherwise pending (orange clock).
          // Green is the colour for BOTH paid states; the icon SHAPE carries the
          // external-vs-settled distinction (sync glyph = external).
          const ps = booking.pricing?.paymentStatus;
          // A partially-refunded booking stays paid + on the calendar (returnStatus 'partial');
          // it still shows the turquoise refund glyph so the partial return is visible at a glance.
          const isPartialReturn = booking.returnStatus === 'partial';
          const isReturned = ps === 'credited' || ps === 'refunded' || isPartialReturn;
          const isExternalPaid = ps === 'paid' && !isPartialReturn && booking.pricing?.paidOutsidePlatform === true;
          return (
            <div
              className="icon-payment-status"
              title={
                isPartialReturn ? 'Partially refunded'
                : isReturned ? (ps === 'credited' ? 'Returned as credit' : 'Refunded')
                : isExternalPaid ? 'Paid externally (reconcile later)'
                : ps === 'paid' ? 'Paid'
                : 'Payment pending'
              }
            >
              <Icon
                path={
                  isReturned ? mdiCreditCardRefund
                  : isExternalPaid ? mdiCreditCardSyncOutline
                  : ps === 'paid' ? mdiCreditCardCheck
                  : mdiCreditCardClockOutline
                }
                size={0.65}
                color={
                  isReturned ? 'var(--dl-color-turquoise-default)'
                  : isExternalPaid ? 'var(--dl-color-greentick)'
                  : ps === 'paid' ? 'var(--dl-color-greentick)'
                  : 'var(--dl-color-needs-action)'
                }
              />
            </div>
          );
        })()}

        {/* Compact mode: only payment status shown above, nothing else */}
        {!compact && (
          <>
            {/* Manual Booking Indicator (non-account user) */}
            {isUnlinkedManualBooking && (
              <div className="actionable-icon-button icon-manual-booking" title="Non-account user booking">
                <Icon path={mdiAccountQuestionOutline} size={0.7} />
              </div>
            )}

            {/* Edit Icon */}
            {!hideEditIcon && (
              <button
                className="actionable-icon-button icon-edit"
                onClick={handleEditClick}
                title="View details"
              >
                <Icon path={mdiPencil} size={0.7} />
              </button>
            )}

            {/* Message Icon - 3-state system (see getMessageIconState for logic) */}
            <button
              className={`actionable-icon-button icon-message ${messageIconState.className}`}
              onClick={handleMessageClick}
              title={messageIconState.title}
            >
              <Icon path={messageIconState.icon} size={1.0} />
            </button>

            {/* Confirm Icon (only show for pending, outline style) */}
            {isPending && (
              <button
                className="actionable-icon-button icon-checkbox"
                onClick={handleCheckboxClick}
                title="Confirm booking"
              >
                <Icon path={mdiCheckOutline} size={0.7} />
              </button>
            )}

            {/* Travel Icon */}
            {showTravelIcon && (
              <div className="icon-travel" title="Travel booking">
                <Icon path={mdiAirplane} size={0.7} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BookingMiniCard;
